import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  Alert,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Snackbar,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { FilterColumn } from '../../../../types/filters';

export interface FilterHeaderMismatchPayload {
  headerLabel: string;
  columnId: string;
  resolvedFilterKey: string;
  filterColumns: FilterColumn[];
}

type MismatchContextValue = {
  showFilterColumnMismatch: (payload: FilterHeaderMismatchPayload) => void;
};

const FilterHeaderMismatchContext = createContext<MismatchContextValue | null>(null);

export const useFilterHeaderMismatch = (): MismatchContextValue => {
  const ctx = useContext(FilterHeaderMismatchContext);
  if (!ctx) {
    return {
      showFilterColumnMismatch: () => {},
    };
  }
  return ctx;
};

export const FilterHeaderMismatchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [payload, setPayload] = useState<FilterHeaderMismatchPayload | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const showFilterColumnMismatch = useCallback((p: FilterHeaderMismatchPayload) => {
    setPayload(p);
    setSnackbarOpen(true);
    setPopoverAnchor(null);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
    setPopoverAnchor(null);
  }, []);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
  }, []);

  const value = useMemo(
    () => ({ showFilterColumnMismatch }),
    [showFilterColumnMismatch]
  );

  return (
    <FilterHeaderMismatchContext.Provider value={value}>
      {children}
      {payload ? (
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={8000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: '24px !important',
          }}
        >
          <Alert
            severity="info"
            variant="filled"
            onClose={handleCloseSnackbar}
            sx={{
              minWidth: 300,
              alignItems: 'center',
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Detalhes da coluna e chaves de filtro"
                  onClick={handleOpenPopover}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Fechar"
                  onClick={handleCloseSnackbar}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            Esta coluna não tem filtro correspondente. Toque no ícone de informação para ver os detalhes.
          </Alert>
        </Snackbar>
      ) : null}

      <Popover
        open={Boolean(popoverAnchor) && Boolean(payload)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, p: 2 },
          },
        }}
      >
        {payload ? (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cabeçalho
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
              {payload.headerLabel}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              ID da coluna na tabela
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {payload.columnId}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Chave tentada (API do filtro)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {payload.resolvedFilterKey}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Chaves de filtro disponíveis nesta tela
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
              {payload.filterColumns.map((col) => (
                <ListItem key={col.value} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={col.label}
                    secondary={
                      <Typography component="span" variant="caption" fontFamily="monospace">
                        {col.value}
                      </Typography>
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}
      </Popover>
    </FilterHeaderMismatchContext.Provider>
  );
};
